import { Colors } from "@/constants/Colors";
import { FontFamilies } from "@/constants/Typography";
import { BottomTabBarProps } from "@react-navigation/bottom-tabs";
import React from "react";
import { Text, TouchableOpacity, View, Image } from "react-native";
import { StyleSheet } from "@/utils/appStyleSheet";
import useWindow from "@/hooks/useWindow";
import Icons from "@/assets/icons";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { router } from "expo-router";
import { BlurView } from "expo-blur";
import { LinearGradient } from "expo-linear-gradient";

type TabbarProps = BottomTabBarProps;

const TabbarMobile = (props: TabbarProps) => {
  const { state, descriptors, navigation } = props;
  const insets = useSafeAreaInsets();
  const handleChatPress = () => {
    router.push("/chat");
  };

  return (
    <View style={[styles.container, { paddingBottom: insets.bottom + 8 }]}>
      <LinearGradient
        colors={["transparent", "rgba(0,0,0,0.5)"]}
        style={styles.gradientBackground}
        pointerEvents="none"
      />
      <View style={styles.tabbarWrapper}>
        <View style={styles.tabContainerShadow}>
          <View style={styles.tabContainer}>
            <BlurView intensity={20} tint="light" style={styles.blurFill} />
            <View style={styles.tabContainerInner}>
              {state.routes.map((route, index) => {
                const isFocused = state.index === index;
                const { options } = descriptors[route.key];
                const renderIcon = options.tabBarIcon;

                const onPress = () => {
                  const event = navigation.emit({
                    type: "tabPress",
                    target: route.key,
                    canPreventDefault: true,
                  });

                  if (!isFocused && !event.defaultPrevented) {
                    navigation.navigate(route.name);
                  }
                };

                const displayLabel =
                  route.name === "feeds"
                    ? "My Claims"
                    : route.name === "my-money"
                    ? "My Money"
                    : "Account";

                return (
                  <TouchableOpacity
                    key={route.key}
                    style={styles.tab}
                    onPress={onPress}
                    activeOpacity={0.8}
                  >
                    {renderIcon?.({
                      color: isFocused ? Colors.primary : "#B0B0B0",
                      focused: isFocused,
                      size: 28,
                    })}
                    <Text
                      style={[
                        styles.tabText,
                        isFocused && styles.activeTabText,
                      ]}
                    >
                      {displayLabel}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
        </View>

        <TouchableOpacity
          style={styles.chatButton}
          onPress={handleChatPress}
          activeOpacity={0.8}
        >
          <Image
            source={require("@/assets/icons/claim-chat.png")}
            style={styles.chatButtonImage}
            resizeMode="cover"
          />
        </TouchableOpacity>
      </View>
    </View>
  );
};

const TabbarDesktop = (props: BottomTabBarProps) => {
  const { state, descriptors, navigation } = props;

  return (
    <View style={[styles.containerDesktop]}>
      <Icons.LogoWithTextBlack width={114} height={28} />
      <View style={styles.tabContainerDesktop}>
        {state.routes.map((route, index) => {
          const isFocused = state.index === index;
          const { options } = descriptors[route.key];
          const renderIcon = options.tabBarIcon;

          const label =
            options.tabBarLabel !== undefined
              ? typeof options.tabBarLabel === "string"
                ? options.tabBarLabel
                : options.title || route.name
              : options.title !== undefined
              ? options.title
              : route.name;

          const onPress = () => {
            const event = navigation.emit({
              type: "tabPress",
              target: route.key,
              canPreventDefault: true,
            });

            if (!isFocused && !event.defaultPrevented) {
              navigation.navigate(route.name);
            }
          };

          return (
            <TouchableOpacity
              key={route.key}
              style={[styles.tabDesktop]}
              onPress={onPress}
              activeOpacity={0.8}
            >
              {renderIcon?.({
                color: isFocused ? Colors.primary : Colors.icon,
                focused: isFocused,
                size: 20,
              })}
              <Text
                style={[
                  styles.tabTextDesktop,
                  isFocused && styles.activeTabText,
                ]}
              >
                {label}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>
    </View>
  );
};

const Tabbar = (props: TabbarProps) => {
  const { isDesktop } = useWindow();

  if (isDesktop) {
    return <TabbarDesktop {...props} />;
  }

  return <TabbarMobile {...props} />;
};

export default Tabbar;

const styles = StyleSheet.create({
  container: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    alignItems: "center",
    paddingHorizontal: 16,
    paddingTop: 8,
  },
  gradientBackground: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  tabbarWrapper: {
    flexDirection: "row",
    alignItems: "center",
    gap: 0,
  },
  tabContainerShadow: {
    borderRadius: 105,
    shadowColor: "#000",
    shadowOffset: { width: 2, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 10,
    elevation: 4,
  },
  tabContainer: {
    borderRadius: 105,
    overflow: "hidden",
    borderWidth: 3,
    borderColor: "#FFFFFF",
  },
  blurFill: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  tabContainerInner: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "rgba(255,255,255,0.9)",
    paddingVertical: 8,
    paddingLeft: 8,
    paddingRight: 16,
  },
  tab: {
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 4,
    paddingHorizontal: 4,
    width: 79,
    gap: 2,
  },
  tabText: {
    fontSize: 10,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#B0B0B0",
    textAlign: "center",
  },
  activeTabText: {
    color: Colors.primary,
  },
  chatButton: {
    width: 72,
    height: 72,
    borderRadius: 36,
    marginLeft: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.12,
    shadowRadius: 8,
    elevation: 8,
  },
  chatButtonImage: {
    width: 72,
    height: 72,
    borderRadius: 36,
  },
  // Desktop styles
  containerDesktop: {
    borderRightWidth: 1,
    borderRightColor: "#E5E7EB",
    paddingRight: 64,
    paddingLeft: 16,
    paddingTop: 16,
    alignItems: "flex-start",
  },
  tabContainerDesktop: {
    paddingTop: 40,
    paddingLeft: 24,
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 12,
  },
  tabDesktop: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 12,
    paddingHorizontal: 12,
    gap: 8,
  },
  tabTextDesktop: {
    fontSize: 14,
    fontFamily: FontFamilies.InterTight.SemiBold,
    color: "#B2B2B2",
  },
});
